import { EmployeeRecord } from '@/utils/employee-indexeddb';
import {
  HEADQUARTER_FILTER_PREFIX,
  DEPARTMENT_FILTER_PREFIX,
  parseDepartmentHierarchy
} from '@/utils/department-utils';
import { OrganizationInfo } from '../types';

export const buildIdentifierVariants = (value?: string | null): string[] => {
  if (!value) {
    return [];
  }

  const seen = new Set<string>();
  const queue: string[] = [value];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || seen.has(current)) {
      continue;
    }
    seen.add(current);

    const lowered = current.toLowerCase();
    if (!seen.has(lowered)) {
      queue.push(lowered);
    }

    if (current.startsWith('no-usage-')) {
      queue.push(current.slice('no-usage-'.length));
    }

    const colonIndex = current.indexOf(':');
    if (colonIndex !== -1 && colonIndex < current.length - 1) {
      queue.push(current.slice(colonIndex + 1));
    }
  }

  return Array.from(seen);
};

export const dedupeHierarchy = (values: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach(value => {
    const trimmed = value?.trim();
    if (!trimmed) return;
    if (seen.has(trimmed)) return;
    seen.add(trimmed);
    result.push(trimmed);
  });
  return result;
};

export const getOrganizationInfo = (employee?: EmployeeRecord | null): OrganizationInfo => {
  if (!employee) {
    return {
      hierarchy: [],
      display: '—'
    };
  }

  let headquarter = employee.headquarter?.trim();
  let departmentUnit = employee.departmentUnit?.trim();
  let normalizedDepartment = employee.normalizedDepartment?.trim();
  let hierarchy = dedupeHierarchy(employee.departmentHierarchy || []);

  const needsParse = hierarchy.length === 0 || !headquarter || (!departmentUnit && hierarchy.length > 1) || !normalizedDepartment;
  if (needsParse) {
    const parsed = parseDepartmentHierarchy(employee.department);
    if (!headquarter && parsed.headquarter) headquarter = parsed.headquarter.trim();
    if (!departmentUnit && parsed.department) departmentUnit = parsed.department.trim();
    if (hierarchy.length === 0 && parsed.hierarchy.length > 0) {
      hierarchy = dedupeHierarchy(parsed.hierarchy);
    }
    if (!normalizedDepartment && parsed.normalized) {
      normalizedDepartment = parsed.normalized.trim();
    }
  }

  if (hierarchy.length === 0) {
    hierarchy = dedupeHierarchy([
      headquarter,
      departmentUnit,
      normalizedDepartment,
      employee.department
    ]);
  }

  if (!headquarter && hierarchy.length > 0) {
    headquarter = hierarchy[0];
  }
  if (!departmentUnit && hierarchy.length > 1) {
    departmentUnit = hierarchy[hierarchy.length - 1];
  }

  const displayParts = hierarchy.length > 0
    ? hierarchy
    : dedupeHierarchy([normalizedDepartment, employee.department]);

  const display = displayParts.length > 0 ? displayParts.join(' / ') : '—';

  return {
    headquarter,
    departmentUnit,
    normalizedDepartment,
    hierarchy: displayParts,
    display
  };
};

export const getOrganizationLines = (employee?: EmployeeRecord | null): string[] => {
  const info = getOrganizationInfo(employee);
  if (info.hierarchy.length > 0) {
    return info.hierarchy;
  }
  return info.display !== '—' ? [info.display] : ['—'];
};

export const matchesOrganizationFilter = (employee: EmployeeRecord | undefined, filterValue: string): boolean => {
  if (!filterValue || filterValue === 'all') {
    return true;
  }

  const normalizedFilter = filterValue === '未使用者' ? 'no-usage' : filterValue;

  if (normalizedFilter === 'no-usage') {
    return true;
  }

  if (normalizedFilter === 'no-mapping') {
    return !employee;
  }

  if (!employee) {
    return false;
  }

  const info = getOrganizationInfo(employee);

  if (normalizedFilter.startsWith(HEADQUARTER_FILTER_PREFIX)) {
    const selectedHQ = normalizedFilter.slice(HEADQUARTER_FILTER_PREFIX.length);
    return info.headquarter === selectedHQ;
  }

  if (normalizedFilter.startsWith(DEPARTMENT_FILTER_PREFIX)) {
    const parts = normalizedFilter.split('::');
    const selectedHQ = parts[1] || '';
    const selectedDepartmentUnit = parts[2] || '';

    if (selectedHQ && info.headquarter !== selectedHQ) {
      return false;
    }

    if (!selectedDepartmentUnit) {
      return true;
    }

    return info.hierarchy.some(level => level === selectedDepartmentUnit || level.includes(selectedDepartmentUnit));
  }

  const trimmedFilter = normalizedFilter.trim();
  const searchTargets = dedupeHierarchy([
    info.display,
    info.headquarter,
    info.departmentUnit,
    info.normalizedDepartment,
    employee.department,
    ...info.hierarchy
  ]);

  return searchTargets.some(target => target.includes(trimmedFilter));
};
